import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  Box, 
  Container, 
  Typography, 
  Paper, 
  Grid, 
  TextField, 
  Button, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  FormHelperText,
  Snackbar,
  Alert,
  Divider,
  Stepper,
  Step,
  StepLabel,
  useTheme,
  Tooltip,
  Chip
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker, TimePicker } from '@mui/x-date-pickers';
import { format, addDays, isAfter, isBefore, addHours, parseISO, isToday } from 'date-fns';
import { FaUtensils, FaGlassMartiniAlt, FaCalendarAlt, FaUsers, FaCheck, FaInfoCircle, FaClock } from 'react-icons/fa';
import { motion } from 'framer-motion';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

// Theater locations from info.md
const theaterLocations = [
  {
    id: 'downtown',
    name: 'Downtown',
    address: '123 Main Street, Downtown',
    hours: {
      weekday: '12PM-11PM',
      weekend: '10AM-1AM'
    }
  },
  {
    id: 'westside',
    name: 'Westside Mall',
    address: '789 Shopping Lane, Westside',
    hours: {
      weekday: '11AM-11PM',
      weekend: '11AM-11PM'
    }
  },
  {
    id: 'lakefront',
    name: 'Lakefront',
    address: '456 Lake Drive, Eastside',
    hours: {
      weekday: '2PM-10PM',
      weekend: '12PM-12AM'
    }
  }
];

// Modified generateTimeSlots function to include visual time slot data
const generateTimeSlots = (date, locationId) => {
  const isWeekend = date.getDay() === 0 || date.getDay() === 6;
  
  // Different start/end times based on location and weekday/weekend
  const startHours = {
    downtown: isWeekend ? 10 : 12,
    westside: 11,
    lakefront: isWeekend ? 12 : 14
  };
  
  const endHours = {
    downtown: isWeekend ? 24 : 23,
    westside: 23,
    lakefront: isWeekend ? 24 : 22
  };
  
  if (!locationId) {
    return {
      startHours,
      endHours,
      slots: []
    };
  }
  
  const locationStart = startHours[locationId];
  const locationEnd = endHours[locationId];
  
  const slots = [];
  for (let hour = locationStart; hour < locationEnd; hour++) {
    // Generate time slots every 30 minutes
    const fullHourSlot = new Date(date);
    fullHourSlot.setHours(hour, 0, 0, 0);
    
    const halfHourSlot = new Date(date);
    halfHourSlot.setHours(hour, 30, 0, 0);
    
    // Determine popularity/status based on time of day
    let fullHourStatus = 'available';
    let halfHourStatus = 'available';
    
    // Peak hours (lunch and dinner)
    if ((hour >= 12 && hour <= 13) || (hour >= 18 && hour <= 20)) {
      fullHourStatus = Math.random() > 0.3 ? 'popular' : 'limited';
      halfHourStatus = Math.random() > 0.4 ? 'popular' : 'limited';
    }
    
    // Add some random booked slots
    if (Math.random() > 0.85) {
      fullHourStatus = 'booked';
    }
    if (Math.random() > 0.85) {
      halfHourStatus = 'booked';
    }
    
    slots.push({
      time: fullHourSlot,
      status: fullHourStatus
    });
    
    slots.push({
      time: halfHourSlot,
      status: halfHourStatus
    });
  }
  
  return {
    startHours,
    endHours,
    slots
  };
};

const Reservation = () => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const reservationType = queryParams.get('type') || 'restaurant';
  
  // Define animation variants
  const pageVariants = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.4 } },
    exit: { opacity: 0, y: -20, transition: { duration: 0.3 } }
  };
  
  const itemVariants = {
    initial: { opacity: 0, y: 15 },
    animate: i => ({ 
      opacity: 1, 
      y: 0, 
      transition: { 
        delay: i * 0.1,
        duration: 0.4
      } 
    }),
  };
  
  // Initialize step process
  const [activeStep, setActiveStep] = useState(0);
  const steps = ['Location & Time', 'Party Details', 'Your Information', 'Confirmation'];
  
  // Form state
  const [formState, setFormState] = useState({
    location: '',
    date: addDays(new Date(), 1), // Default to tomorrow
    time: null,
    partySize: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    specialRequests: ''
  });
  
  // Validation state
  const [errors, setErrors] = useState({});
  
  // Success message state
  const [successMessage, setSuccessMessage] = useState(false);
  
  // Generate available time slots based on selected date and location
  const [availableTimeSlots, setAvailableTimeSlots] = useState([]);
  const [timeSlotData, setTimeSlotData] = useState([]);
  
  // Animation state for confirmation
  const [isConfirmed, setIsConfirmed] = useState(false);
  
  useEffect(() => {
    if (formState.location && formState.date) {
      const { startHours, endHours, slots } = generateTimeSlots(formState.date, formState.location);
      
      // Set traditional time slots for the TimePicker
      const pickerSlots = slots.map(slot => slot.time);
      setAvailableTimeSlots(pickerSlots);
      
      // Set the visual time slot data
      setTimeSlotData(slots);
    }
  }, [formState.location, formState.date]);
  
  useEffect(() => {
    if (activeStep === 3) {
      // Trigger the confirmation animation after a short delay
      const timer = setTimeout(() => {
        setIsConfirmed(true);
      }, 300);
      
      return () => clearTimeout(timer);
    }
  }, [activeStep]);
  
  // Handle form changes
  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setFormState({
      ...formState,
      [field]: value
    });
    
    // Clear error when field is updated
    if (errors[field]) {
      setErrors({
        ...errors,
        [field]: null
      });
    }
  };
  
  // Handle date and time picker changes
  const handleDateChange = (newDate) => {
    setFormState({
      ...formState,
      date: newDate,
      time: null // Reset time when date changes
    });
    
    if (errors.date) {
      setErrors({
        ...errors,
        date: null
      });
    }
  };
  
  const handleTimeChange = (newTime) => {
    setFormState({
      ...formState,
      time: newTime
    });
    
    if (errors.time) {
      setErrors({
        ...errors,
        time: null
      });
    }
  };
  
  // Validate form step
  const validateStep = (step) => {
    const newErrors = {};
    
    if (step === 0) {
      if (!formState.location) newErrors.location = 'Please select a location';
      if (!formState.date) newErrors.date = 'Please select a date';
      if (!formState.time) newErrors.time = 'Please select a time';
    } else if (step === 1) {
      if (!formState.partySize) newErrors.partySize = 'Please select party size';
    } else if (step === 2) {
      if (!formState.firstName) newErrors.firstName = 'First name is required';
      if (!formState.lastName) newErrors.lastName = 'Last name is required';
      if (!formState.email) {
        newErrors.email = 'Email is required';
      } else if (!/\S+@\S+\.\S+/.test(formState.email)) {
        newErrors.email = 'Please enter a valid email';
      }
      if (!formState.phone) {
        newErrors.phone = 'Phone number is required';
      } else if (!/^\d{10}$/.test(formState.phone.replace(/\D/g, ''))) {
        newErrors.phone = 'Please enter a valid 10-digit phone number';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // Handle next step
  const handleNext = () => {
    if (validateStep(activeStep)) {
      if (activeStep === steps.length - 2) {
        // Submit form
        // In a real app, this would send data to the server
        setSuccessMessage(true);
      }
      setActiveStep((prev) => prev + 1);
    }
  };
  
  // Handle back step
  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };
  
  // Reset form and navigate back to dining page
  const handleStartOver = () => {
    setActiveStep(0);
    setFormState({
      location: '',
      date: addDays(new Date(), 1),
      time: null,
      partySize: '',
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      specialRequests: ''
    });
    navigate('/dining');
  };
  
  // Get reservation title based on type
  const getReservationTitle = () => {
    switch (reservationType) {
      case 'restaurant':
        return 'Restaurant Reservation';
      case 'bar':
        return 'Bar Lounge Reservation';
      default:
        return 'Dining Reservation';
    }
  };
  
  // Get reservation icon based on type
  const getReservationIcon = () => {
    switch (reservationType) {
      case 'restaurant':
        return <FaUtensils size={32} color={theme.palette.secondary.main} />;
      case 'bar':
        return <FaGlassMartiniAlt size={32} color={theme.palette.secondary.main} />;
      default:
        return <FaUtensils size={32} color={theme.palette.secondary.main} />;
    }
  };
  
  // Render different form steps
  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <motion.div
            initial="initial"
            animate="animate"
            variants={pageVariants}
          >
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <motion.div custom={0} variants={itemVariants}>
                  <FormControl 
                    fullWidth 
                    error={!!errors.location}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#1e3a5f', 
                        borderRadius: 1
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255, 255, 255, 0.3)',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255, 255, 255, 0.5)',
                      },
                      '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#f5b400',
                        borderWidth: 2
                      },
                      '& .MuiSelect-icon': {
                        color: '#f5b400'
                      },
                      '& .MuiInputLabel-root': {
                        color: 'rgba(255, 255, 255, 0.7)'
                      },
                      '& .MuiInputLabel-root.Mui-focused': {
                        color: '#f5b400'
                      }
                    }}
                  >
                    <InputLabel id="location-label">Theater Location</InputLabel>
                    <Select
                      labelId="location-label"
                      id="location"
                      value={formState.location}
                      label="Theater Location"
                      onChange={handleChange('location')}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            bgcolor: '#1e3a5f',
                            borderRadius: 2,
                            boxShadow: '0 8px 16px rgba(0,0,0,0.4)',
                            border: '1px solid rgba(255,255,255,0.1)',
                            '& .MuiMenuItem-root': {
                              py: 1.5,
                              borderBottom: '1px solid rgba(255,255,255,0.05)',
                              '&:last-child': {
                                borderBottom: 'none'
                              }
                            },
                            '& .MuiMenuItem-root.Mui-selected': {
                              bgcolor: 'rgba(245,180,0,0.15)',
                              '&:hover': {
                                bgcolor: 'rgba(245,180,0,0.25)'
                              }
                            },
                            '& .MuiMenuItem-root:hover': {
                              bgcolor: 'rgba(255,255,255,0.05)'
                            }
                          }
                        }
                      }}
                    >
                      {theaterLocations.map((theater) => (
                        <MenuItem key={theater.id} value={theater.id}>
                          {theater.name} - {theater.address}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.location && <FormHelperText sx={{ color: 'error.main', fontWeight: 500 }}>{errors.location}</FormHelperText>}
                  </FormControl>
                </motion.div>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <motion.div custom={1} variants={itemVariants}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      label="Reservation Date"
                      value={formState.date}
                      onChange={handleDateChange}
                      minDate={addDays(new Date(), 1)}
                      maxDate={addDays(new Date(), 30)}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!errors.date,
                          helperText: errors.date,
                          sx: {
                            '& .MuiInputBase-root': {
                              backgroundColor: '#1e3a5f',
                              borderRadius: 1
                            },
                            '& .MuiInputBase-input': {
                              color: '#ffffff',
                            },
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'rgba(255, 255, 255, 0.3)',
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'rgba(255, 255, 255, 0.5)',
                            },
                            '& .MuiInputAdornment-root .MuiButtonBase-root': {
                              color: '#f5b400'
                            }
                          }
                        },
                        popper: {
                          sx: {
                            '& .MuiPaper-root': {
                              bgcolor: '#1e3a5f',
                              color: '#ffffff',
                              borderRadius: 2,
                              boxShadow: '0 8px 20px rgba(0,0,0,0.4)',
                              border: '1px solid rgba(255,255,255,0.1)',
                              '& .MuiPickersDay-root': {
                                color: '#ffffff',
                                '&.Mui-selected': {
                                  backgroundColor: '#f5b400',
                                  color: '#000000',
                                  fontWeight: 'bold'
                                },
                              },
                              '& .MuiDayCalendar-weekDayLabel': {
                                color: 'rgba(255,255,255,0.7)'
                              },
                              '& .MuiPickersCalendarHeader-label': {
                                color: '#ffffff'
                              },
                              '& .MuiIconButton-root': {
                                color: '#f5b400'
                              }
                            }
                          }
                        }
                      }}
                    />
                  </LocalizationProvider>
                </motion.div>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <motion.div custom={2} variants={itemVariants}>
                  <Tooltip 
                    title={!formState.location || !formState.date ? "Please select a location and date first" : ""}
                    arrow
                    placement="top"
                  >
                    <Box sx={{ width: '100%' }}>
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <TimePicker
                          label="Reservation Time"
                          value={formState.time}
                          onChange={handleTimeChange}
                          disabled={!formState.location || !formState.date}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              error: !!errors.time,
                              helperText: errors.time,
                              sx: {
                                '& .MuiInputBase-root': {
                                  backgroundColor: '#1e3a5f',
                                  borderRadius: 1,
                                  '&.Mui-disabled': {
                                    backgroundColor: 'rgba(30, 58, 95, 0.5)',
                                    color: 'rgba(255,255,255,0.5)',
                                    '& .MuiOutlinedInput-notchedOutline': {
                                      borderColor: 'rgba(255, 255, 255, 0.2)'
                                    }
                                  }
                                },
                                '& .MuiInputBase-input': {
                                  color: '#ffffff',
                                },
                                '& .MuiInputLabel-root': {
                                  color: 'rgba(255, 255, 255, 0.7)',
                                  '&.Mui-disabled': {
                                    color: 'rgba(255, 255, 255, 0.5)'
                                  }
                                },
                                '& .MuiOutlinedInput-notchedOutline': {
                                  borderColor: 'rgba(255, 255, 255, 0.3)',
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                  borderColor: 'rgba(255, 255, 255, 0.5)',
                                },
                                '& .MuiInputAdornment-root .MuiButtonBase-root': {
                                  color: '#f5b400'
                                },
                                '& .MuiInputAdornment-root .MuiButtonBase-root.Mui-disabled': {
                                  color: 'rgba(255,255,255,0.4)'
                                }
                              }
                            },
                            popper: {
                              sx: {
                                '& .MuiPaper-root': {
                                  bgcolor: '#1e3a5f',
                                  color: '#ffffff',
                                  borderRadius: 2,
                                  boxShadow: '0 8px 20px rgba(0,0,0,0.4)',
                                  border: '1px solid rgba(255,255,255,0.1)',
                                  '& .MuiClockNumber-root': {
                                    color: '#ffffff',
                                  },
                                  '& .MuiClockPointer-root': {
                                    backgroundColor: '#f5b400',
                                  },
                                  '& .MuiClockPointer-thumb': {
                                    backgroundColor: '#f5b400',
                                    borderColor: '#f5b400',
                                  },
                                }
                              }
                            }
                          }}
                        />
                      </LocalizationProvider>
                    </Box>
                  </Tooltip>
                </motion.div>
              </Grid>
              
              {formState.location && formState.date && (
                <Grid item xs={12}>
                  <motion.div 
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto', transition: { duration: 0.4 } }}
                  >
                    <Typography variant="h6" sx={{ mt: 3, mb: 2, fontWeight: 600 }}>
                      Available Time Slots
                    </Typography>
                    
                    <Paper sx={{ 
                      p: 2, 
                      bgcolor: 'rgba(30, 58, 95, 0.6)', 
                      borderRadius: 2,
                      border: '1px solid rgba(255,255,255,0.1)'
                    }}>
                      <Box sx={{ display: 'flex', mb: 2, alignItems: 'center', gap: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#4CAF50', mr: 1 }}/>
                          <Typography variant="caption">Available</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#FFC107', mr: 1 }}/>
                          <Typography variant="caption">Limited</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#f44336', mr: 1 }}/>
                          <Typography variant="caption">Booked</Typography>
                        </Box>
                      </Box>
                      
                      <Box sx={{ 
                        display: 'flex', 
                        flexWrap: 'wrap', 
                        gap: 1.5 
                      }}>
                        {timeSlotData.map((slot, idx) => {
                          const timeString = format(slot.time, 'h:mm a');
                          const isSelected = formState.time && 
                            format(formState.time, 'h:mm a') === timeString;
                          
                          // Determine chip color based on status
                          let chipColor = '';
                          let chipBg = '';
                          switch(slot.status) {
                            case 'booked':
                              chipColor = 'rgba(244, 67, 54, 0.6)';
                              chipBg = 'rgba(244, 67, 54, 0.15)';
                              break;
                            case 'limited':
                              chipColor = 'rgba(255, 193, 7, 0.8)';
                              chipBg = 'rgba(255, 193, 7, 0.15)';
                              break;
                            case 'popular':
                              chipColor = '#f5b400';
                              chipBg = 'rgba(245, 180, 0, 0.15)';
                              break;
                            default:
                              chipColor = 'rgba(76, 175, 80, 0.8)';
                              chipBg = 'rgba(76, 175, 80, 0.15)';
                          }
                          
                          return (
                            <motion.div
                              key={timeString}
                              custom={idx * 0.02}
                              initial={{ opacity: 0, scale: 0.9 }}
                              animate={{ 
                                opacity: 1, 
                                scale: 1,
                                transition: { 
                                  delay: idx * 0.02,
                                  duration: 0.3
                                } 
                              }}
                            >
                              <Chip
                                label={timeString}
                                onClick={() => slot.status !== 'booked' && handleTimeChange(slot.time)}
                                disabled={slot.status === 'booked'}
                                icon={<FaClock />}
                                sx={{
                                  bgcolor: isSelected ? 'rgba(245, 180, 0, 0.25)' : chipBg,
                                  color: isSelected ? '#ffffff' : chipColor,
                                  border: isSelected ? '2px solid #f5b400' : '1px solid ' + chipColor,
                                  fontWeight: isSelected ? 600 : 400,
                                  '&:hover': {
                                    bgcolor: slot.status !== 'booked' ? 'rgba(255, 255, 255, 0.1)' : chipBg,
                                    transform: slot.status !== 'booked' ? 'scale(1.05)' : 'none',
                                  },
                                  transition: 'all 0.2s',
                                  cursor: slot.status === 'booked' ? 'not-allowed' : 'pointer',
                                  px: 1,
                                  '& .MuiChip-label': {
                                    px: 1
                                  },
                                  '& .MuiChip-icon': {
                                    color: isSelected ? '#f5b400' : chipColor
                                  }
                                }}
                              />
                            </motion.div>
                          );
                        })}
                      </Box>
                    </Paper>
                  </motion.div>
                </Grid>
              )}
              
              {formState.location && (
                <Grid item xs={12}>
                  <motion.div custom={3} variants={itemVariants}>
                    <Box 
                      sx={{ 
                        mt: 2, 
                        p: 3, 
                        bgcolor: 'rgba(30, 58, 95, 0.6)', 
                        borderRadius: 2,
                        border: '1px solid rgba(255,255,255,0.1)',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1.5
                      }}
                    >
                      <FaInfoCircle size={20} color="#f5b400" style={{ marginTop: 2 }} />
                      <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mb: 1, color: 'rgba(255,255,255,0.9)' }}>
                          {reservationType === 'restaurant' ? 'Restaurant' : 'Bar'} hours at {
                            theaterLocations.find(t => t.id === formState.location)?.name
                          }:
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ opacity: 0.8 }}>
                          Weekdays: {theaterLocations.find(t => t.id === formState.location)?.hours.weekday}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ opacity: 0.8 }}>
                          Weekends: {theaterLocations.find(t => t.id === formState.location)?.hours.weekend}
                        </Typography>
                      </Box>
                    </Box>
                  </motion.div>
                </Grid>
              )}
            </Grid>
          </motion.div>
        );
        
      case 1:
        return (
          <motion.div
            initial="initial"
            animate="animate"
            variants={pageVariants}
          >
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <motion.div custom={0} variants={itemVariants}>
                  <FormControl 
                    fullWidth 
                    error={!!errors.partySize}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#1e3a5f', 
                        borderRadius: 1
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255, 255, 255, 0.3)',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255, 255, 255, 0.5)',
                      },
                      '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#f5b400',
                        borderWidth: 2
                      },
                      '& .MuiSelect-icon': {
                        color: '#f5b400'
                      },
                      '& .MuiInputLabel-root': {
                        color: 'rgba(255, 255, 255, 0.7)'
                      },
                      '& .MuiInputLabel-root.Mui-focused': {
                        color: '#f5b400'
                      }
                    }}
                  >
                    <InputLabel id="party-size-label">Party Size</InputLabel>
                    <Select
                      labelId="party-size-label"
                      id="party-size"
                      value={formState.partySize}
                      label="Party Size"
                      onChange={handleChange('partySize')}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            bgcolor: '#1e3a5f',
                            borderRadius: 2,
                            boxShadow: '0 8px 16px rgba(0,0,0,0.4)',
                            border: '1px solid rgba(255,255,255,0.1)',
                            '& .MuiMenuItem-root': {
                              py: 1.5,
                              borderBottom: '1px solid rgba(255,255,255,0.05)',
                              '&:last-child': {
                                borderBottom: 'none'
                              }
                            },
                            '& .MuiMenuItem-root.Mui-selected': {
                              bgcolor: 'rgba(245,180,0,0.15)',
                              '&:hover': {
                                bgcolor: 'rgba(245,180,0,0.25)'
                              }
                            },
                            '& .MuiMenuItem-root:hover': {
                              bgcolor: 'rgba(255,255,255,0.05)'
                            }
                          }
                        }
                      }}
                    >
                      {[...Array(8)].map((_, i) => (
                        <MenuItem key={i} value={i + 1}>
                          {i + 1} {i === 0 ? 'person' : 'people'}
                        </MenuItem>
                      ))}
                      <MenuItem value="9+">9+ people (large party)</MenuItem>
                    </Select>
                    {errors.partySize && <FormHelperText sx={{ color: 'error.main', fontWeight: 500 }}>{errors.partySize}</FormHelperText>}
                  </FormControl>
                </motion.div>
              </Grid>
              
              <Grid item xs={12}>
                <motion.div custom={1} variants={itemVariants}>
                  <TextField
                    id="special-requests"
                    label="Special Requests or Dietary Requirements"
                    multiline
                    rows={4}
                    value={formState.specialRequests}
                    onChange={handleChange('specialRequests')}
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#1e3a5f',
                        borderRadius: 1
                      },
                      '& .MuiInputLabel-root': {
                        color: 'rgba(255, 255, 255, 0.7)'
                      },
                      '& .MuiInputLabel-root.Mui-focused': {
                        color: '#f5b400'
                      }
                    }}
                  />
                </motion.div>
              </Grid>
              
              <Grid item xs={12}>
                <motion.div custom={2} variants={itemVariants}>
                  <Box 
                    sx={{ 
                      mt: 2, 
                      p: 3, 
                      bgcolor: 'rgba(30, 58, 95, 0.6)', 
                      borderRadius: 2,
                      border: '1px solid rgba(255,255,255,0.1)',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5
                    }}
                  >
                    <FaInfoCircle size={20} color="#f5b400" style={{ marginTop: 2 }} />
                    <Typography variant="body2" color="text.secondary" sx={{ opacity: 0.9 }}>
                      {reservationType === 'restaurant' ? 
                        'Our staff will prepare your table based on your party size. For parties larger than 8, please arrive 15 minutes early.' :
                        'For the bar lounge, we\'ll reserve appropriate seating based on your party size. For parties larger than 8, please consider our private lounge.'
                      }
                    </Typography>
                  </Box>
                </motion.div>
              </Grid>
            </Grid>
          </motion.div>
        );
        
      case 2:
        return (
          <motion.div
            initial="initial"
            animate="animate"
            variants={pageVariants}
          >
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <motion.div custom={0} variants={itemVariants}>
                  <TextField
                    id="first-name"
                    label="First Name"
                    value={formState.firstName}
                    onChange={handleChange('firstName')}
                    fullWidth
                    error={!!errors.firstName}
                    helperText={errors.firstName}
                    required
                  />
                </motion.div>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <motion.div custom={1} variants={itemVariants}>
                  <TextField
                    id="last-name"
                    label="Last Name"
                    value={formState.lastName}
                    onChange={handleChange('lastName')}
                    fullWidth
                    error={!!errors.lastName}
                    helperText={errors.lastName}
                    required
                  />
                </motion.div>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <motion.div custom={2} variants={itemVariants}>
                  <TextField
                    id="email"
                    label="Email Address"
                    type="email"
                    value={formState.email}
                    onChange={handleChange('email')}
                    fullWidth
                    error={!!errors.email}
                    helperText={errors.email}
                    required
                  />
                </motion.div>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <motion.div custom={3} variants={itemVariants}>
                  <TextField
                    id="phone"
                    label="Phone Number"
                    value={formState.phone}
                    onChange={handleChange('phone')}
                    fullWidth
                    error={!!errors.phone}
                    helperText={errors.phone || "For reservation notifications"}
                    required
                  />
                </motion.div>
              </Grid>
              
              <Grid item xs={12}>
                <motion.div custom={4} variants={itemVariants}>
                  <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      We'll send your reservation confirmation and any updates to both your email and phone. 
                      Your information is only used for reservation purposes.
                    </Typography>
                  </Box>
                </motion.div>
              </Grid>
            </Grid>
          </motion.div>
        );
        
      case 3:
        return (
          <motion.div
            initial="initial"
            animate="animate"
            variants={pageVariants}
          >
            <Grid container spacing={3}>
              <Grid item xs={12} sx={{ textAlign: 'center' }}>
                <motion.div
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ 
                    scale: isConfirmed ? 1 : 0, 
                    opacity: isConfirmed ? 1 : 0,
                    transition: { 
                      type: "spring", 
                      stiffness: 260, 
                      damping: 20,
                      delay: 0.2 
                    }
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      mb: 3,
                      position: 'relative'
                    }}
                  >
                    <Box
                      component={motion.div}
                      sx={{
                        width: 80,
                        height: 80,
                        borderRadius: '50%',
                        bgcolor: 'success.main',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        boxShadow: '0 4px 20px rgba(76, 175, 80, 0.4)'
                      }}
                      initial={{ scale: 0.8 }}
                      animate={{ 
                        scale: [0.8, 1.1, 1],
                        transition: { 
                          duration: 0.4, 
                          times: [0, 0.6, 1],
                          delay: 0.4
                        }
                      }}
                    >
                      <motion.div
                        initial={{ opacity: 0, pathLength: 0 }}
                        animate={{ 
                          opacity: 1, 
                          pathLength: 1,
                          transition: { delay: 0.6, duration: 0.6 }
                        }}
                      >
                        <FaCheck size={40} color="white" />
                      </motion.div>
                    </Box>
                    
                    {/* Add decorative glowing circles */}
                    {[...Array(6)].map((_, i) => (
                      <Box
                        component={motion.div}
                        key={i}
                        sx={{
                          position: 'absolute',
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          bgcolor: '#4CAF50',
                          opacity: 0.7,
                          filter: 'blur(4px)'
                        }}
                        initial={{ 
                          x: 0, 
                          y: 0, 
                          opacity: 0 
                        }}
                        animate={{ 
                          x: [0, (Math.random() - 0.5) * 160],
                          y: [0, (Math.random() - 0.5) * 160],
                          opacity: [0, 0.7, 0],
                          transition: { 
                            duration: 1.5 + Math.random(),
                            delay: 0.6 + i * 0.1,
                            repeat: Infinity,
                            repeatType: 'loop'
                          }
                        }}
                      />
                    ))}
                  </Box>
                </motion.div>
                
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ 
                    opacity: 1, 
                    y: 0,
                    transition: { delay: 0.6, duration: 0.4 }
                  }}
                >
                  <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, color: 'success.main' }}>
                    Reservation Confirmed!
                  </Typography>
                </motion.div>
                
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ 
                    opacity: 1,
                    transition: { delay: 0.8, duration: 0.4 }
                  }}
                >
                  <Typography variant="h6" sx={{ mb: 4, fontWeight: 500 }}>
                    Thank you for your reservation, {formState.firstName}!
                  </Typography>
                </motion.div>
              </Grid>
              
              <Grid item xs={12}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ 
                    opacity: 1, 
                    y: 0,
                    transition: { delay: 0.9, duration: 0.5 }
                  }}
                >
                  <Paper 
                    sx={{ 
                      p: 3, 
                      bgcolor: 'background.paper', 
                      borderRadius: 2,
                      boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
                      border: '1px solid rgba(255,255,255,0.1)'
                    }}
                  >
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                          Reservation Details
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                      </Grid>
                      
                      <Grid item xs={6} sm={4}>
                        <motion.div custom={0} variants={itemVariants}>
                          <Typography variant="body2" color="text.secondary">
                            Reservation Type
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {reservationType === 'restaurant' ? 'Restaurant Dining' : 'Bar Lounge'}
                          </Typography>
                        </motion.div>
                      </Grid>
                      
                      <Grid item xs={6} sm={4}>
                        <motion.div custom={1} variants={itemVariants}>
                          <Typography variant="body2" color="text.secondary">
                            Location
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {theaterLocations.find(t => t.id === formState.location)?.name}
                          </Typography>
                        </motion.div>
                      </Grid>
                      
                      <Grid item xs={6} sm={4}>
                        <motion.div custom={2} variants={itemVariants}>
                          <Typography variant="body2" color="text.secondary">
                            Party Size
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {formState.partySize} {formState.partySize === 1 ? 'person' : 'people'}
                          </Typography>
                        </motion.div>
                      </Grid>
                      
                      <Grid item xs={6} sm={4}>
                        <motion.div custom={3} variants={itemVariants}>
                          <Typography variant="body2" color="text.secondary">
                            Date
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {format(formState.date, 'EEEE, MMMM d, yyyy')}
                          </Typography>
                        </motion.div>
                      </Grid>
                      
                      <Grid item xs={6} sm={4}>
                        <motion.div custom={4} variants={itemVariants}>
                          <Typography variant="body2" color="text.secondary">
                            Time
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {format(formState.time, 'h:mm a')}
                          </Typography>
                        </motion.div>
                      </Grid>
                      
                      <Grid item xs={6} sm={4}>
                        <motion.div custom={5} variants={itemVariants}>
                          <Typography variant="body2" color="text.secondary">
                            Confirmation #
                          </Typography>
                          <Typography variant="body1" sx={{ 
                            fontWeight: 600,
                            fontFamily: 'monospace',
                            letterSpacing: 1,
                            color: '#f5b400' 
                          }}>
                            {`SC${Math.floor(Math.random() * 900000) + 100000}`}
                          </Typography>
                        </motion.div>
                      </Grid>
                      
                      {formState.specialRequests && (
                        <Grid item xs={12}>
                          <motion.div custom={6} variants={itemVariants}>
                            <Typography variant="body2" color="text.secondary">
                              Special Requests
                            </Typography>
                            <Typography variant="body1">
                              {formState.specialRequests}
                            </Typography>
                          </motion.div>
                        </Grid>
                      )}
                    </Grid>
                  </Paper>
                </motion.div>
              </Grid>
              
              <Grid item xs={12}>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ 
                    opacity: 1,
                    transition: { delay: 1.2, duration: 0.4 }
                  }}
                >
                  <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      A confirmation email has been sent to {formState.email}. If you need to cancel or modify your reservation, 
                      please contact us at least 2 hours prior to your reservation time.
                    </Typography>
                  </Box>
                </motion.div>
              </Grid>
            </Grid>
          </motion.div>
        );
        
      default:
        return null;
    }
  };
  
  return (
    <Box>
      <Navbar />
      
      {/* Hero Section */}
      <Box
        sx={{
          py: 6,
          backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.7), rgba(10, 25, 41, 0.9))',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', color: 'white' }}>
            <Box 
              sx={{ 
                display: 'flex', 
                justifyContent: 'center',
                mb: 2
              }}
            >
              {getReservationIcon()}
            </Box>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 700,
                mb: 2,
              }}
            >
              {getReservationTitle()}
            </Typography>
            <Typography variant="h6" sx={{ maxWidth: '800px', mx: 'auto', opacity: 0.9 }}>
              {reservationType === 'restaurant'
                ? 'Reserve your table for in-theater dining at Stellar Cinemas'
                : 'Reserve your space at our Cosmic Bar before or after your movie'}
            </Typography>
          </Box>
        </Container>
      </Box>
      
      {/* Reservation Form Section */}
      <Box sx={{ py: 6, bgcolor: 'background.default' }}>
        <Container maxWidth="md">
          <Paper 
            sx={{ 
              p: { xs: 3, md: 5 }, 
              borderRadius: 2,
              boxShadow: '0 10px 30px rgba(0,0,0,0.25)',
              border: '1px solid rgba(255,255,255,0.1)',
            }}
          >
            <Stepper 
              activeStep={activeStep} 
              sx={{ 
                mb: 5,
                '& .MuiStepLabel-label': {
                  color: 'text.secondary', // default color
                  fontSize: '0.9rem',
                  mt: 0.5,
                },
                '& .MuiStepLabel-label.Mui-active': {
                  color: '#f5b400', // gold color for active step
                  fontWeight: 700,
                },
                '& .MuiStepLabel-label.Mui-completed': {
                  color: 'text.primary', // white for completed steps
                },
                '& .MuiStepIcon-root': {
                  color: 'rgba(255,255,255,0.2)', // faded for inactive
                  fontSize: 32,
                },
                '& .MuiStepIcon-root.Mui-active': {
                  color: '#f5b400', // gold for active
                },
                '& .MuiStepIcon-root.Mui-completed': {
                  color: 'rgba(255,255,255,0.5)', // semi-transparent white for completed
                },
                '& .MuiStepConnector-line': {
                  borderColor: 'rgba(255,255,255,0.1)',
                },
              }}
            >
              {steps.map((label, index) => (
                <Step key={label}>
                  <StepLabel StepIconComponent={({ active, completed }) => (
                    <Box
                      component={motion.div}
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        bgcolor: active 
                          ? '#f5b400' 
                          : completed 
                            ? 'rgba(255,255,255,0.5)' 
                            : 'rgba(255,255,255,0.2)',
                        border: active ? '2px solid #f5b400' : 'none',
                        boxShadow: active ? '0 0 10px rgba(245,180,0,0.5)' : 'none',
                        color: active ? '#000' : '#fff',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        transition: 'all 0.3s ease'
                      }}
                      animate={{
                        scale: active ? [1, 1.2, 1] : 1,
                        transition: {
                          duration: 0.5,
                          times: [0, 0.5, 1],
                          repeat: active ? Infinity : 0,
                          repeatDelay: 2
                        }
                      }}
                    >
                      {completed ? <FaCheck size={16} /> : index + 1}
                    </Box>
                  )}>
                    {label}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
            
            <Box>
              {renderStepContent(activeStep)}
            </Box>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 5 }}>
              {activeStep === steps.length - 1 ? (
                <>
                  <Button 
                    onClick={handleStartOver} 
                    variant="outlined"
                    sx={{
                      borderWidth: 2,
                      borderColor: 'rgba(255,255,255,0.3)',
                      color: 'rgba(255,255,255,0.9)',
                      '&:hover': {
                        borderWidth: 2,
                        borderColor: 'rgba(255,255,255,0.5)',
                        backgroundColor: 'rgba(255,255,255,0.05)'
                      }
                    }}
                  >
                    Return to Dining
                  </Button>
                  <Button 
                    component={motion.button}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => navigate('/')} 
                    variant="contained" 
                    color="secondary"
                    sx={{
                      fontWeight: 600,
                      boxShadow: '0 4px 10px rgba(245,180,0,0.3)'
                    }}
                  >
                    Back to Home
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    disabled={activeStep === 0}
                    onClick={handleBack}
                    variant="outlined"
                    sx={{
                      borderWidth: 2,
                      borderColor: 'rgba(255,255,255,0.3)',
                      color: 'rgba(255,255,255,0.9)',
                      '&:hover': {
                        borderWidth: 2,
                        borderColor: 'rgba(255,255,255,0.5)',
                        backgroundColor: 'rgba(255,255,255,0.05)'
                      },
                      '&.Mui-disabled': {
                        borderColor: 'rgba(255,255,255,0.15)',
                        color: 'rgba(255,255,255,0.4)'
                      }
                    }}
                  >
                    Back
                  </Button>
                  <Button 
                    component={motion.button}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleNext} 
                    variant="contained" 
                    color="secondary"
                    sx={{
                      fontWeight: 600,
                      boxShadow: '0 4px 10px rgba(245,180,0,0.3)',
                      '&:hover': {
                        boxShadow: '0 6px 15px rgba(245,180,0,0.4)',
                        transform: 'translateY(-2px)'
                      },
                      transition: 'all 0.3s ease'
                    }}
                  >
                    {activeStep === steps.length - 2 ? 'Complete Reservation' : 'Next'}
                  </Button>
                </>
              )}
            </Box>
          </Paper>
        </Container>
      </Box>
      
      <Footer />
      
      {/* Success Snackbar */}
      <Snackbar 
        open={successMessage} 
        autoHideDuration={6000} 
        onClose={() => setSuccessMessage(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSuccessMessage(false)} 
          severity="success"
          variant="filled"
          sx={{ width: '100%' }}
        >
          Reservation successfully created!
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Reservation; 